import {
  VM_DISCLOSURE,
  VM_SMS_CONFIRMATION,
  VM_SMS_OFFER,
  VmPatient,
  VmSettings,
  VmSmsReason,
  Voicemail,
} from '@models';

/** Mirrors the API's VoicemailBookingSmsTemplate. Demo only; the real text is served. */
function demoSmsMessage(providerName: string, link: string): string {
  return `Thanks for contacting ${providerName}'s office. You can view availability and schedule here: ${link} Sent via iCareDx. Replies are not monitored. Reply STOP to opt out or HELP for help.`;
}

export const VM_DEMO_PATIENTS: VmPatient[] = [
  {
    id: 'jane',
    name: 'Jane Smith',
    phone: '(503) 555-0123',
    dob: '1985-04-12',
  },
  {
    id: 'michael',
    name: 'Michael Johnson',
    phone: '(503) 555-2468',
    dob: '1978-09-03',
  },
  {
    id: 'sarah',
    name: 'Sarah Williams',
    phone: '(503) 555-9876',
    dob: '1990-11-18',
  },
  {
    id: 'david',
    name: 'David Brown',
    phone: '(503) 555-0146',
    dob: '1969-06-27',
  },
  {
    id: 'kevin',
    name: 'Kevin Garcia',
    phone: '(503) 555-0186',
    dob: '1982-02-07',
  },
  {
    id: 'amanda',
    name: 'Amanda Martinez',
    phone: '(503) 555-0176',
    dob: '1993-07-14',
  },
  {
    id: 'robert',
    name: 'Robert Lee',
    phone: '(503) 555-0110',
    dob: '1974-12-01',
  },
  {
    id: 'taylor',
    name: 'Taylor Wilson',
    phone: '(503) 555-0198',
    dob: '1988-05-21',
  },
  {
    id: 'tyler',
    name: 'Tyler Wilson',
    phone: '(503) 555-0109',
    dob: '1991-08-09',
  },
];

export const VM_DEMO_LANDING_PAGE = 'https://example.com/providers/alex-reed';

export function createDemoSettings(providerName = 'Your practice'): VmSettings {
  return {
    state: 'active',
    smsAvailable: true,
    disclosure: VM_DISCLOSURE,
    disclosureVersion: '2026-09-14',
    smsOffer: VM_SMS_OFFER,
    smsConfirmation: VM_SMS_CONFIRMATION,
    smsMessage: demoSmsMessage(providerName, '[booking link]'),
    maxGreetingLength: 500,
    useVoicemailNumberOnWebsite: true,
    phoneNumber: '(503) 555-0199',
    practicePhone: '(503) 555-0100',
    providerName,
    greeting: `Hello, you've reached ${providerName}. Please leave your name, phone number, and a brief message. I'll get back to you as soon as possible.`,
    overrideUrl: '',
    bookingLink: { url: VM_DEMO_LANDING_PAGE, source: 'landing-page' },
  };
}

/** Booking-text outcomes per sample call. Everything else was sent when the caller pressed 1. */
const NOT_SENT: Partial<Record<string, VmSmsReason>> = {
  david: 'appointment-booked',
};

/** [id, patientId, minutesAgo, durationSeconds, transcript] */
const ENTRIES: [string, string | null, number, number, string][] = [
  [
    'jane',
    'jane',
    15,
    10,
    'Hi Doctor Reed, this is Jane Smith. I would like to schedule a follow-up appointment next week. An afternoon would work best for me. Please call me back when you have a chance. Thank you.',
  ],
  ['michael', 'michael', 90, 11, ''],
  [
    'unknown',
    null,
    1440,
    10,
    'Hello, I am calling to ask if your practice is accepting new patients. I would like to learn about your next available appointment. Please call me back at the number I called from. Thank you.',
  ],
  ['sarah', 'sarah', 1530, 9, ''],
  [
    'david',
    'david',
    2880,
    6,
    'Hi, this is David Brown. I need to move my appointment to next week. Please call me back when you can.',
  ],
  [
    'kevin',
    'kevin',
    3060,
    7,
    'Hi Dr. Reed, Kevin here. Thank you for your help at our last visit. I would like to schedule a follow-up appointment.',
  ],
  [
    'amanda',
    'amanda',
    4320,
    6,
    'Hi, this is Amanda. I have a question about my next appointment. Please give me a call. Thanks.',
  ],
  [
    'robert',
    'robert',
    4740,
    5,
    'Hello, Robert Lee calling. I am checking the time of my next appointment. Thank you.',
  ],
  [
    'jane-earlier',
    'jane',
    8640,
    5,
    'Hi Dr. Reed, this is Jane. I would like to confirm our appointment for next week. Thank you.',
  ],
];

/** Transcript text, keyed by voicemail id. Served by its own call, as the real API serves it. */
export function createDemoTranscripts(): Record<string, string> {
  return Object.fromEntries(ENTRIES.map(([id, , , , text]) => [id, text]));
}

export function createDemoVoicemails(
  now = new Date(),
  providerName = 'Alex Reed'
): Voicemail[] {
  const ago = (minutes: number) =>
    new Date(now.getTime() - minutes * 60_000).toISOString();

  return ENTRIES.map(([id, patientId, minutesAgo, duration]) => {
    const receivedAt = ago(minutesAgo);
    const patient =
      VM_DEMO_PATIENTS.find(item => item.id === patientId) ?? null;
    const callerNumber = patient?.phone ?? '(503) 555-0198';
    const notSent = NOT_SENT[id];
    return {
      id,
      patient: patient ? { ...patient } : null,
      callerNumber,
      receivedAt,
      duration,
      recordingRef: `/assets/voicemail/voicemail-${id}.mp3`,
      unread: id === 'michael' || id === 'sarah',
      transcript: {
        status:
          id === 'michael' ? 'processing' : id === 'sarah' ? 'failed' : 'ready',
      },
      sms: notSent
        ? {
            status: 'not-sent',
            reason: notSent,
            attemptedAt: null,
            sentAt: null,
            destination: callerNumber,
            message: null,
          }
        : {
            status: id === 'sarah' ? 'rejected' : 'sent',
            reason: null,
            attemptedAt: receivedAt,
            sentAt: id === 'sarah' ? null : receivedAt,
            destination: callerNumber,
            message: demoSmsMessage(providerName, VM_DEMO_LANDING_PAGE),
          },
      matchType: patient ? 'automatic' : 'unknown',
    };
  });
}
